// src/ui/theme/ThemeRuntimeProvider.js
// Capa de tema runtime del editor de estilos (Capa 1, F1.2 + F4 por pagina).
//
// Maneja overrides en dos niveles (cascada):
//   resuelto(pagina) = pageOverrides[pagina]  ??  base overrides  ??  default
// Inyecta el resultado para la pagina ACTUAL en :root. La pagina actual la marca
// <RouteScopeWatcher> (dentro del Router) segun la ruta navegada.
//
// Strangler: sin overrides => defaults actuales => web identica. Con el flag
// STYLES_RUNTIME en false => NO-OP. Doc: design-system/styles-editor/ARCHITECTURE.md.
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { useLocation } from "react-router-dom";
import { SEMANTIC_TOKENS, defaultCssVars } from "./semanticTokens";
import { STYLES_RUNTIME } from "../featureFlags";

const ThemeRuntimeContext = createContext(null);

function clearAllVars() {
  const root = document.documentElement;
  Object.values(SEMANTIC_TOKENS).forEach(({ cssVar }) =>
    root.style.removeProperty(cssVar),
  );
}

// /portal/teacher/salas -> teacher/salas
export function deriveScopeKey(pathname) {
  if (!pathname) return null;
  const m = pathname.replace(/^\/+/, "").replace(/^portal\//, "");
  return m || null;
}

// Mapa { "--edu-...": valor } resuelto a partir de un token map de overrides.
function resolveCssVars(overrideMap) {
  const out = { ...defaultCssVars() };
  Object.entries(overrideMap).forEach(([token, value]) => {
    const def = SEMANTIC_TOKENS[token];
    if (def && value != null && value !== "") out[def.cssVar] = value;
  });
  return out;
}

export function ThemeRuntimeProvider({ children }) {
  const [overrides, setOverrides] = useState({}); // base (token map)
  const [pageOverrides, setPageOverrides] = useState({}); // { pageKey: token map }
  const [currentPage, setCurrentPage] = useState(null);

  // base + overrides de la pagina actual.
  const resolvedOverrides = useMemo(() => {
    const merged = { ...overrides };
    const pg = currentPage && pageOverrides[currentPage];
    if (pg) Object.assign(merged, pg);
    return merged;
  }, [overrides, pageOverrides, currentPage]);

  const cssVars = useMemo(
    () => resolveCssVars(resolvedOverrides),
    [resolvedOverrides],
  );

  useEffect(() => {
    if (!STYLES_RUNTIME) {
      clearAllVars();
      return undefined;
    }
    const root = document.documentElement;
    Object.entries(cssVars).forEach(([k, v]) => root.style.setProperty(k, v));
    return clearAllVars;
  }, [cssVars]);

  // scope = "base" o un pageKey.
  const setTokenOverride = useCallback((token, value, scope = "base") => {
    if (scope === "base") {
      setOverrides((o) => ({ ...o, [token]: value }));
    } else {
      setPageOverrides((p) => ({
        ...p,
        [scope]: { ...(p[scope] || {}), [token]: value },
      }));
    }
  }, []);

  const clearTokenOverride = useCallback((token, scope = "base") => {
    if (scope === "base") {
      setOverrides((o) => {
        const next = { ...o };
        delete next[token];
        return next;
      });
    } else {
      setPageOverrides((p) => {
        const sc = { ...(p[scope] || {}) };
        delete sc[token];
        return { ...p, [scope]: sc };
      });
    }
  }, []);

  const resetOverrides = useCallback(() => {
    setOverrides({});
    setPageOverrides({});
  }, []);

  const applyOverrides = useCallback((baseObj = {}, pagesObj = {}) => {
    setOverrides(baseObj || {});
    setPageOverrides(pagesObj || {});
  }, []);

  const api = useMemo(
    () => ({
      enabled: STYLES_RUNTIME,
      tokens: SEMANTIC_TOKENS,
      overrides, // base
      pageOverrides,
      currentPage,
      resolvedOverrides,
      resolveCssVars, // helper para el preview por scope del editor
      setCurrentPage,
      setTokenOverride,
      clearTokenOverride,
      resetOverrides,
      applyOverrides,
    }),
    [
      overrides,
      pageOverrides,
      currentPage,
      resolvedOverrides,
      setTokenOverride,
      clearTokenOverride,
      resetOverrides,
      applyOverrides,
    ],
  );

  useEffect(() => {
    if (!STYLES_RUNTIME) return undefined;
    window.eduThemeRuntime = api;
    return () => {
      delete window.eduThemeRuntime;
    };
  }, [api]);

  return (
    <ThemeRuntimeContext.Provider value={api}>
      {children}
    </ThemeRuntimeContext.Provider>
  );
}

export function useThemeRuntime() {
  const ctx = useContext(ThemeRuntimeContext);
  if (!ctx) {
    throw new Error(
      "useThemeRuntime debe usarse dentro de ThemeRuntimeProvider",
    );
  }
  return ctx;
}

// Resuelve un slot de imagen (clave `image.<slot>`) con fallback al asset por
// defecto. Considera la cascada base + pagina actual.
export function useImageSlot(slot, fallback) {
  const { resolvedOverrides } = useThemeRuntime();
  const value = resolvedOverrides[`image.${slot}`];
  return typeof value === "string" && value.trim() ? value : fallback;
}

// Sincroniza la ruta navegada con el provider para aplicar overrides por pagina.
// Debe montarse DENTRO del Router.
export function RouteScopeWatcher() {
  const { setCurrentPage } = useThemeRuntime();
  const location = useLocation();
  useEffect(() => {
    setCurrentPage(deriveScopeKey(location.pathname));
  }, [location.pathname, setCurrentPage]);
  return null;
}

export default ThemeRuntimeProvider;
